import React, { useState, useEffect } from "react";
import { Switch, Route, Redirect, Router } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import {
    Settings as SettingIcon,
    PictureAsPdf as ReportsIcon,
    Security as AdminIcon,
    LocalShipping as OrderIcon,
    Dashboard as DashboardIcon,
    Devices as ProductIcon,
    Code,
    MonetizationOn as BillingIcon,
    CloudDownload as DownloadIcon,
    StarHalf as StarHalfIcon,
    Apartment as ClientIcon,
    AccountBoxSharp as AccountBoxSharpIcon,
    Tune as TuneIcon,
    Book as BookIcon

    // Commute as CommuteIcon
} from "@material-ui/icons";
import GetAppIcon from '@material-ui/icons/GetApp';
import { connect } from "react-redux";

import Dashboard from "./modules/np-dashboard/Dashboard/Dashboard";
import Login from "./modules/sc-users/Login";
import * as authActions from "./modules/sc-core/Auth/store/authActions";
import * as statisticActions from "./modules/sc-dashboard/Statistic/store/statisticActions";
import History from "./History";

import PasswordExpired from "./modules/sc-users/ChangePassword";
import Unsubscribe from "./modules/sc-users/Unsubscribe";
import UnsubscribeConfirm from "./modules/sc-users/Unsubscribe/UnsubscribeConfirm"
import { isSuperUser, hasExternalSettingsAvailable, getLandingPath } from "./modules/sc-utilities";
import { adminTheme, normalUserTheme } from './themes'
// Custom securaze components
import { subComponents as settingsSubComponents } from "./modules/sc-settings";
import { subComponents as adminSubComponents } from "./modules/sc-admin";
import { subComponents as productsSubComponents } from "./modules/sc-product";
import { subComponents as billingSubComponents } from "./modules/sc-billing";
import { subComponents as statisticSubComponents } from "./modules/sc-dashboard";
import { subComponents as downloadSubComponents } from "./modules/sc-download";
import { subComponents as reportSubComponents } from "./modules/sc-reports";
import { subComponents as logisticSubComponents } from "./modules/sc-logistic";
import { subComponents as unpairedSubComponents } from "./modules/sc-unpairedAssets";
import { subComponents as accountManagementSubComponents } from "./modules/sc-accountManagement";
import { subComponents as advancedSubComponents } from "./modules/sc-advanced";
import { subComponents as manualsSubComponents } from "./modules/sc-manuals-page";
import { subComponents as downloadsSubComponents } from "./modules/sc-download-page/DownloadPage";

import ErrorPage from "./modules/sc-error/ErrorPage";
import ErrorBoundary from "./modules/sc-error/ErrorBoundary";

import EmailAuthentication from './modules/sc-enterpriseAuthentication';

import { routes as trialVoucherActivationRoutes } from "./modules/sc-trialVoucher/CustomerRegistration";
import { routes as modulesRoutes } from './modules/sc-routes'

import { Dialog, DialogTitle, DialogContent, Typography, Divider, MuiThemeProvider, createMuiTheme } from "@material-ui/core";
import Shared from "./modules/sc-shared";
import AppLogoLoading from './modules/sc-logo';
import UserIdle from './modules/sc-userIdle';
import HubSpotChat from "../src/modules/sc-utilities/HubSpotChat";
import CreateUser from "./modules/sc-users/OpenIDConnect/CreateUser";
import OpenIDLogin from "./modules/sc-users/OpenIDConnect/OpenIDLogin";
import config from "../src/config/config";

import SessionExpiredModal from "./modules/sc-users/SessionExpired";

import { snackActions } from './modules/sc-utilities/SnackBar';

const styles = theme => ({
    root: {
        display: "block",
        position: "relative",
        height: "100%",
        top: 0
    },
    root1: {
        display: "block",
        position: "fixed",
        height: "100%",
        width: '100%',
        overflow: 'hidden',
        top: 0

    },
    paper: {
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "hidden"
    },
    logo: {
        width: '250px',
        margin: '10px'
    },
});


const customPasswordForm = () => {
    return <PasswordExpired />;
};
const customUnsubscribe = () => {
    return <Unsubscribe />;
};
const customUnsubscribeConfirm = () => {
    return <UnsubscribeConfirm />;
};
const customShared = () => {
    return <Shared />;
};
let counter = 10;

const App = props => {
    const { classes, globalLoading, isAuthenticated, onLogout, AutoLogout, permissions, passwordExpired, socket, translations, location, activeCustomer, onSaveLocation } = props;
    const theme = createMuiTheme(isSuperUser() ? adminTheme : normalUserTheme);

    const [globalSearch, setGlobalSearch] = useState(null);

    const [error, setError] = useState(10);
    const [globalSearchInfoOpen, setGlobalSearchInfoOpen] = useState(false);
    const { openHandler, hasLoaded, closeHandler } = HubSpotChat(config.hubspot_id);

    const handleNPGlobalSearch = params => {
        params.searchInput = params.searchInput?.replace(/['()*]/g, "#");

        let queryParams = [];
        
        for (const param in params) {
            queryParams.push(`${param}=${encodeURIComponent(params[param])}`)
        }

        setGlobalSearch({ to: `/search?${queryParams.join('&')}` });
    };

    useEffect(() => {
        if (globalSearch) {
            setGlobalSearch(null);
        }
    }, [globalSearch]);

    const handleNPGlobalSearchInfo = () => {
        setGlobalSearchInfoOpen(!globalSearchInfoOpen);
    };

    useEffect(() => {
        let timer = null;

        timer = setInterval(() => {
            if (globalLoading && counter > 0) {
                counter = (counter - 1);
                setError(counter)
            }
        }, 1000);
        return () => clearInterval(timer);

    }, [globalLoading]);

    let sidebarRoutes = [
        {
            key: "dashboard",
            name: translations["dashboard"] || "Dashboard",
            icon: DashboardIcon,
            permissions: ["online_devices", "all_assets_view", "overview"],
            basePath: "/dashboard",
            subComponents: statisticSubComponents(translations)
        },
        {
            key: "assets",
            name: translations["products"] || "Assets",
            icon: ProductIcon,
            permissions: ["product"],
            basePath: "/products",
            subComponents: productsSubComponents(translations)
        },
        {
            key: "reports",
            name: translations["reports"] || "Reports",
            icon: ReportsIcon,
            permissions: ['erasure_report', 'diagnose_report', "report"],
            basePath: "/reports",
            subComponents: reportSubComponents(translations)
        },
        {
            key: "logistic",
            name: translations["logistic"] || "Logistic",
            icon: OrderIcon,
            permissions: ["logistic_view"],
            basePath: "/logistic",
            subComponents: logisticSubComponents(translations),
        },
        {
            key: "downloads",
            name: translations["downloads"] || "Downloads",
            icon: GetAppIcon,
            permissions: ["binary_image_download"],
            path: "/download-page",
            subComponents: downloadsSubComponents(translations)
        },
        {
            key: "user_manuals",
            name: translations["user_manuals"] || "User Manuals",
            icon: BookIcon,
            permissions: [],
            basePath: "/manuals-page",
            subComponents: manualsSubComponents(translations)
        },
        {
            key: "binary_images",
            name: translations["binary_images"] || "Binary Images",
            icon: DownloadIcon,
            permissions: ["access_all_binary_images"],
            basePath: "/downloads",
            subComponents: downloadSubComponents(translations)
        },
        {
            key: "unpaired_assets",
            name: translations["unpaired_assets"] || "Unpaired Assets",
            icon: StarHalfIcon,
            permissions: ["product", "unpaired_palette"],
            basePath: "/unpairedAssets",
            subComponents: unpairedSubComponents(translations),
        },
        {
            key: "account_management",
            name: translations["account_management"] || "Account management",
            icon: AccountBoxSharpIcon,
            permissions: ["account_management_menu_option"],
            basePath: "/account-management",
            subComponents: accountManagementSubComponents(translations)
        },
        {
            key: "settings",
            name: translations["settings"] || "Settings",
            icon: SettingIcon,
            permissions: ["printer", "network_zone_list", "customer_erasure_method_list", "external_system_setting_list", "presets_view", "securaze_api", "installed_software_view", "grading_view", "report_customization_view", "group_list", "customer_diagnose_case_list", "label_template_list", "sku_view"],
            basePath: "/settings",
            subComponents: settingsSubComponents(translations)
        },
        {
            key: "license",
            name: translations["billing_info"] || "License",
            icon: BillingIcon,
            permissions: ["billing_info", "billing_account", "billing_plan", "license_prototype", "trial_voucher_list"],
            basePath: "/billing",
            subComponents: billingSubComponents(translations)
        },
        {
            key: "advanced",
            name: translations["advanced"] || "Advanced",
            icon: TuneIcon,
            permissions: ["print_job_list", "user_session_list"],
            basePath: "/advanced",
            subComponents: advancedSubComponents(translations)
        },
        {
            key: "admin_settings",
            name: translations["admin"] || "Admin Settings",
            icon: AdminIcon,
            permissions: ["dashboard_admin_settings_menu_option"],
            basePath: "/admin",
            subComponents: adminSubComponents(translations)
        },
    ];

    if (
        !(window.location.pathname === "/login" || window.location.pathname === "/logout") &&
        location === "/dashboard/asset-overview" &&
        !isAuthenticated &&
        !(window.location.pathname === '/unsubscribe/init') &&
        !(window.location.pathname === '/unsubscribe/confirm')
    ) {
        onSaveLocation(window.location.href.substring(window.location.origin.length, window.location.href.length));
    }

    if (window.location.href.includes("/trial/verification") || window.location.href.includes("/trial/registration")) {
        return <MuiThemeProvider theme={theme}>{trialVoucherActivationRoutes("/trial")}</MuiThemeProvider>;
    }

    const hasPermission = permission => {
        return permissions.includes(permission) || permissions.includes("*");
    };

    // Where to send the user when no specific location is remembered. Not every role can open
    // the asset overview, and PageNotFound redirects here too - a fixed path loops.
    const landingPath = getLandingPath(permissions);

    const handleLogout = () => {
        return onLogout(props.activeCustomer);
    };

    if (!hasExternalSettingsAvailable()) {
        sidebarRoutes.splice(sidebarRoutes.findIndex(route => route.key === 'unpaired_assets'), 1);
    }

    const sendMessage = (message, variant) => {
        snackActions.snackBar({
            message: message,
            options: {
                variant: variant
            }
        });
    }

    return <Router history={History}>
        {window.location.pathname === "/unsubscribe/init" ? (
            <MuiThemeProvider theme={theme}>
                <div className={classes.root1}>
                    <Switch>
                        <Route path="/unsubscribe/init" exact component={customUnsubscribe} />
                    </Switch>
                </div>
            </MuiThemeProvider>
        ) : window.location.pathname === "/unsubscribe/confirm" ? (
            <div className={classes.root1}>
                <Switch>
                    <Route path="/unsubscribe/confirm" exact component={customUnsubscribeConfirm} />
                </Switch>
            </div>
        ) : window.location.pathname === "/shared" ? (
            <div className={classes.root1}>
                <Switch>
                    <Route path="/shared" exact component={customShared} />
                </Switch>
            </div>
        ) : window.location.pathname === "/openid-login" ? (
            <div className={classes.root}>
                <Switch>
                    <Route path="/openid-login" render={props => <MuiThemeProvider theme={theme}><OpenIDLogin /></MuiThemeProvider>} />
                </Switch>
            </div>
        ) : window.location.pathname === "/openid-callback" ? (
            <div className={classes.root}>
                <Switch>
                    <Route path="/openid-callback" render={props => <MuiThemeProvider theme={theme}><CreateUser translations={translations} classes={classes} /></MuiThemeProvider>} />
                </Switch>
            </div>
        ) : !isAuthenticated ? (
            <div className={classes.root}>
                <Switch>
                    {/* login route */}
                    <Route path="/login" render={props => <MuiThemeProvider theme={theme}><Login socket={socket} /></MuiThemeProvider>} />
                    <Route path="/pre-registered-activation" render={props => <MuiThemeProvider theme={theme}><EmailAuthentication socket={socket} /></MuiThemeProvider>} />
                    {/* unauthenticated users are redirected to login route */}
                    <Redirect to="/login" />
                </Switch>
            </div>
        ) : passwordExpired ? (
            <MuiThemeProvider theme={theme}>
                <div className={classes.root}>

                    <Switch>
                        <Route path="/change-password" exact component={customPasswordForm} />
                        <Redirect to="/change-password" />
                    </Switch>
                </div>
            </MuiThemeProvider>
        ) : (
            <ErrorBoundary>
                <MuiThemeProvider theme={theme}>
                    {AutoLogout ? <UserIdle handleLogout={handleLogout} /> : null}
                    {!globalLoading ?
                        <div className={classes.root}>
                            <Dashboard
                                hasLastSearch={props.hasLastSearch}
                                sendMessage={sendMessage}
                                dashboardStyle={classes.mainContainer}
                                translations={translations}
                                handleNPGlobalSearchInfo={handleNPGlobalSearchInfo}
                                handleNPGlobalSearch={handleNPGlobalSearch}
                                sidebarRoutes={sidebarRoutes}
                                sidebarStyle={{
                                    drawer: classes.drawer,
                                    drawerOpen: classes.drawerOpen,
                                    drawerClose: classes.drawerClose
                                }}
                                appBarOptions={{ myProfile: true, search: hasPermission("global_search"), download: true, help: true, liveChat: true }}
                                activeCustomer={props.activeCustomer}
                                handleLogout={handleLogout}
                                sideBarOpenCustomIcon={<Code />}
                                sideBarCloseCustomIcon={<Code />}
                                securaze={true}
                                openHandler={openHandler}
                                hasLoaded={hasLoaded}
                                closeHandler={closeHandler}
                                hubspotId={config.hubspot_id}
                            >
                                <Switch>
                                    {globalSearch ? <Redirect to={globalSearch.to} /> : null}
                                    <Redirect from="/login" to={!activeCustomer ? '/changeActiveCustomer' : location && location !== "/" ? location : landingPath} />
                                    <Redirect from="/change-password" to={!activeCustomer ? '/changeActiveCustomer' : location && location !== "/" ? location : landingPath} />
                                    <Redirect exact from="/" to={!activeCustomer ? '/changeActiveCustomer' : location && location !== "/" ? location : landingPath} />

                                    <Route
                                        path="/logout"
                                        exact
                                        component={() => {
                                            onLogout(props.activeCustomer);
                                            return null;
                                        }}
                                    />

                                </Switch>

                                {modulesRoutes(permissions)}
                                <SessionExpiredModal />
                            </Dashboard>
                            <Dialog open={globalSearchInfoOpen} fullWidth={true} onClose={handleNPGlobalSearchInfo}>
                                <DialogTitle id="simple-dialog-title">{translations["how_to_use_search"] || "How to use search"}</DialogTitle>
                                <DialogContent>
                                    <Typography variant="h6">{translations["basic_search"] || "Basic search"}</Typography>
                                    <Typography variant="caption">{translations["basic_search_text_0"] || "Enter the desired phrase and press 'Enter' key."}</Typography>
                                    <Typography variant="caption">{translations["basic_search_text_1"] || "Example: my_phrase"}</Typography>
                                    <Divider />
                                    <Typography variant="h6">{translations["advanced_search"] || "Advanced search"}</Typography>
                                    <Typography variant="caption">
                                        {translations["advanced_search_text_0"] ||
                                            "If you didn't get results from basic search use hash character to search for desired phrase"}
                                    </Typography>
                                    <Typography variant="caption">{translations["advanced_search_text_1"] || "Example: #my_phrase#"}</Typography>
                                    <Divider />
                                    <Typography variant="h6">{translations["search_for_dates"] || "Search for dates"} </Typography>
                                    <Typography variant="caption">
                                        {translations["search_for_dates_text_0"] || "To search for the desired date use following syntax YYYY-MM-DD."}
                                    </Typography>
                                    <Typography variant="caption">{translations["search_for_dates_text_1"] || "Example with basic search: 2019-08-10"}</Typography>
                                    <Typography variant="caption">{translations["search_for_dates_text_2"] || "Example with advanced search: #2019-08-10#"}</Typography>
                                    <Divider />
                                    <Typography variant="h6">{translations["search_for_multiple_phrases"] || "Search for multiple phrases"}</Typography>
                                    <Typography variant="caption">
                                        {translations["search_for_multiple_phrases_text_0"] || "To search for multiple phrases use separator ';'."}
                                    </Typography>
                                    <Typography variant="caption">
                                        {translations["search_for_multiple_phrases_text_1"] || "Example: my_phrase1;my_phrase2;my_phrase3"}
                                    </Typography>
                                    <Typography variant="caption">
                                        {translations["search_for_multiple_phrases_text_2"] ||
                                            "You can combine it with advanced search and dates (example, my_phrase1;#my_phrase2#;#2019-08-10#"}
                                    </Typography>
                                </DialogContent>
                            </Dialog>
                        </div> :
                        error > 0 ?
                            <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={true}
                                PaperProps={{
                                    classes: {
                                        root: classes.paper
                                    }
                                }}
                            >
                                <AppLogoLoading />
                            </Dialog> :
                            <ErrorPage />
                    }
                </MuiThemeProvider>
            </ErrorBoundary>
        )}
    </Router>

};

const mapStateToProps = state => {
    return {
        activeCustomer: state.core.login.activeCustomer ? state.core.login.activeCustomer.CustomerID : null,
        isAuthenticated: state.core.login.token !== null,
        AutoLogout: state?.core?.login?.activeCustomer?.AutoLogout,
        globalLoading: state?.core?.login?.globalLoading,
        permissions: state?.core?.login?.permissions || [],
        hasLastSearch: state.statistic.searches.lastSearchParams,
        location: state.statistic.statistics.location,
        passwordExpired: state?.core?.login?.passwordExpired ?? localStorage.getItem('CORE|AUTH|PASSWORD|EXPIRED') ?? false,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {}
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onLogout: (activeCustomerID) => dispatch(authActions.logout(activeCustomerID)),
        onSaveLocation: location => dispatch(statisticActions.setLocationData(location)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(App));
